import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, X } from "lucide-react";
import { cn } from "../../utils/cn";
import { EpicLane, User } from "../../types";
import { useProjects } from "../../context/AppContext";
import { UserMultiSelect } from "../ui/UserMultiSelect";

interface AddEpicModalProps {
    isOpen: boolean;
    onClose: () => void;
    onAdd: (epicData: any) => void;
    selectedProjectId?: string | null;
    users?: User[];
}

export function AddEpicModal({ isOpen, onClose, onAdd, selectedProjectId: defaultProjectId, users = [] }: AddEpicModalProps) {
    const { projects } = useProjects();
    const [formData, setFormData] = useState({
        title: "",
        lane: "Unassigned" as EpicLane,
        so: [] as string[],
        arq: [] as string[],
        sgm: false,
        projectId: defaultProjectId || "",
    });

    // Update projectId when defaultProjectId changes
    useEffect(() => {
        if (defaultProjectId) {
            setFormData(prev => ({ ...prev, projectId: defaultProjectId }));
        }
    }, [defaultProjectId]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.title.trim() || !formData.projectId) return;
        onAdd(formData);
        setFormData({ title: "", lane: "Unassigned", so: [], arq: [], sgm: false, projectId: defaultProjectId || "" });
    };

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/50 z-50"
                        onClick={onClose}
                    />
                    {/* Modal Container - Flexbox centered */}
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            transition={{ type: "spring", stiffness: 300, damping: 30 }}
                            className="w-full max-w-lg bg-white dark:bg-slate-900 rounded-2xl shadow-2xl overflow-hidden pointer-events-auto"
                        >
                            {/* Header */}
                            <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between bg-gradient-to-r from-siemens-petrol to-siemens-petrol-dark">
                                <h2 className="text-lg font-bold text-white flex items-center gap-2">
                                    <Plus className="w-5 h-5" />
                                    Create New Epic
                                </h2>
                                <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/20 transition-colors">
                                    <X className="w-5 h-5 text-white" />
                                </button>
                            </div>

                            {/* Form - Siemens Design System */}
                            <form onSubmit={handleSubmit} className="p-6 space-y-5">
                                {/* Title */}
                                <div className="space-y-1.5">
                                    <label className="flex items-center gap-0.5 text-sm font-normal text-[#5c5c75] dark:text-[#b3b3be]">
                                        Epic Title
                                        <span className="text-siemens-petrol dark:text-[#00ffb9]">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.title}
                                        onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                                        placeholder="Enter epic title..."
                                        className="w-full h-12 px-4 bg-white dark:bg-[#13131a] border border-[#d4d4e1] dark:border-[#3d3d4d] rounded text-base text-siemens-deep-blue dark:text-white placeholder:text-[#5c5c75] dark:placeholder:text-[#6e6e82] shadow-[inset_0px_2px_4px_0px_rgba(0,0,40,0.12)] dark:shadow-[inset_0px_2px_4px_0px_rgba(0,0,0,0.3)] focus:outline-none focus:border-siemens-petrol focus:ring-1 focus:ring-siemens-petrol transition-all"
                                        autoFocus
                                    />
                                </div>

                                {/* Project Selection */}
                                <div className="space-y-1.5">
                                    <label className="flex items-center gap-0.5 text-sm font-normal text-[#5c5c75] dark:text-[#b3b3be]">
                                        Project
                                        <span className="text-siemens-petrol dark:text-[#00ffb9]">*</span>
                                    </label>
                                    <select
                                        value={formData.projectId}
                                        onChange={(e) => setFormData(prev => ({ ...prev, projectId: e.target.value }))}
                                        className="w-full h-12 px-4 bg-white dark:bg-[#13131a] border border-[#d4d4e1] dark:border-[#3d3d4d] rounded text-base text-siemens-deep-blue dark:text-white placeholder:text-[#5c5c75] dark:placeholder:text-[#6e6e82] shadow-[inset_0px_2px_4px_0px_rgba(0,0,40,0.12)] dark:shadow-[inset_0px_2px_4px_0px_rgba(0,0,0,0.3)] focus:outline-none focus:border-siemens-petrol focus:ring-1 focus:ring-siemens-petrol transition-all appearance-none"
                                        style={{ backgroundImage: 'none' }} // Remove default arrow if needed, or keep for clarity
                                    >
                                        <option value="" disabled>Select a project...</option>
                                        {projects.map(project => (
                                            <option key={project.id} value={project.id}>
                                                {project.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Lane Selection */}
                                <div className="space-y-1.5">
                                    <label className="block text-sm font-normal text-[#5c5c75] dark:text-[#b3b3be]">
                                        Initial Lane
                                    </label>
                                    <div className="flex flex-wrap gap-2">
                                        {["Unassigned", "Now", "Next", "Later"].map(lane => (
                                            <button
                                                key={lane}
                                                type="button"
                                                onClick={() => setFormData(prev => ({ ...prev, lane: lane as EpicLane }))}
                                                className={cn(
                                                    "h-10 px-4 rounded text-sm font-medium border transition-all",
                                                    formData.lane === lane
                                                        ? lane === "Now" ? "border-siemens-feedback-green bg-siemens-feedback-green text-white"
                                                            : lane === "Next" ? "border-siemens-feedback-orange bg-siemens-feedback-orange text-white"
                                                                : lane === "Later" ? "border-siemens-feedback-blue bg-siemens-feedback-blue text-white"
                                                                    : "border-siemens-petrol bg-siemens-petrol text-white"
                                                        : "border-[#d4d4e1] dark:border-[#3d3d4d] bg-white dark:bg-[#13131a] text-[#5c5c75] dark:text-[#b3b3be] hover:border-siemens-petrol/50"
                                                )}
                                            >
                                                {lane}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Owner & Architect */}
                                <div className="space-y-4">
                                    <UserMultiSelect
                                        label="Service Owners"
                                        selectedUserIds={formData.so}
                                        users={users}
                                        onChange={(ids) => setFormData(prev => ({ ...prev, so: ids }))}
                                        placeholder="Select service owners..."
                                    />
                                    <UserMultiSelect
                                        label="Architects"
                                        selectedUserIds={formData.arq}
                                        users={users}
                                        onChange={(ids) => setFormData(prev => ({ ...prev, arq: ids }))}
                                        placeholder="Select architects..."
                                    />
                                </div>

                                {/* SGM Toggle - Siemens Switch Style */}
                                <div className="flex items-center gap-3 p-4 bg-[#f5f5fa] dark:bg-[#1a1a24] border border-[#d4d4e1] dark:border-[#3d3d4d] rounded">
                                    <button
                                        type="button"
                                        onClick={() => setFormData(prev => ({ ...prev, sgm: !prev.sgm }))}
                                        className={cn(
                                            "w-11 h-6 rounded-full transition-colors relative flex-shrink-0",
                                            formData.sgm ? "bg-siemens-petrol" : "bg-[#b3b3be] dark:bg-[#4d4d5a]"
                                        )}
                                    >
                                        <span className={cn(
                                            "absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-md transition-all duration-200",
                                            formData.sgm ? "left-[22px]" : "left-0.5"
                                        )} />
                                    </button>
                                    <span className="text-sm font-normal text-siemens-deep-blue dark:text-white">
                                        SGM Direction (Requires Service Group Manager attention)
                                    </span>
                                </div>

                                {/* Actions - Siemens Button Styles */}
                                <div className="flex gap-3 pt-3">
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        className="flex-1 h-12 px-6 text-sm font-semibold text-siemens-petrol dark:text-white bg-transparent border border-siemens-petrol dark:border-[#3d3d4d] rounded hover:bg-siemens-petrol/5 dark:hover:bg-white/5 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={!formData.title.trim() || !formData.projectId}
                                        className={cn(
                                            "flex-1 h-12 px-6 text-sm font-semibold rounded transition-all flex items-center justify-center gap-2",
                                            (formData.title.trim() && formData.projectId)
                                                ? "bg-siemens-petrol text-white hover:bg-siemens-petrol-dark shadow-[0px_4px_8px_0px_rgba(0,0,40,0.16)]"
                                                : "bg-[#d4d4e1] dark:bg-[#3d3d4d] text-[#8e8e9f] cursor-not-allowed"
                                        )}
                                    >
                                        <Plus className="w-4 h-4" />
                                        Create Epic
                                    </button>
                                </div>
                            </form>
                        </motion.div>
                    </div>
                </>
            )}
        </AnimatePresence>
    );
}

export default AddEpicModal;
